
import React, { useState, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MobileHeader from '@/components/mobile/MobileHeader';
import MobileBottomNav from '@/components/mobile/MobileBottomNav';
import RegistrationSuccessHeader from '@/components/registration/RegistrationSuccessHeader';
import SecurityWarning from '@/components/registration/SecurityWarning';
import CredentialsDisplay from '@/components/registration/CredentialsDisplay';
import SeedPhraseDisplay from '@/components/registration/SeedPhraseDisplay';
import ToggleableSection from '@/components/registration/ToggleableSection';
import ActionButtons from '@/components/registration/ActionButtons';
import SecurityTips from '@/components/registration/SecurityTips';
import ConfirmationCheckbox from '@/components/registration/ConfirmationCheckbox';

const InternalRegistrationSuccess = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { username, password, pin, seedPhrase, registrationDate } = location.state || {};
  const [copied, setCopied] = useState(false);
  const [showCredentials, setShowCredentials] = useState(false);
  const [showSeedPhrase, setShowSeedPhrase] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  if (!username || !password || !pin || !seedPhrase) {
    return (
      <div className="flex flex-col min-h-screen bg-black w-full">
        <Header className="hidden md:block" />
        <MobileHeader />
        <main className="flex-grow flex items-center justify-center">
          <div className="text-center text-white">
            <h1 className="text-2xl mb-4">Invalid Registration Data</h1>
            <Link to="/internal-login" className="text-sports-green hover:underline">
              Return to Login
            </Link>
          </div>
        </main>
        <Footer />
        <MobileBottomNav />
      </div>
    );
  }

  const handleCopyCredentials = async () => {
    try {
      const credentialsText = `Account Credentials\n\nUsername: ${username}\nPassword: ${password}\nPIN: ${pin}\n\nRecovery Phrase:\n${seedPhrase.join(' ')}\n\nCreated: ${new Date(registrationDate).toLocaleString()}`;
      await navigator.clipboard.writeText(credentialsText);
      setCopied(true);
      toast.success('All credentials copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const handleDownload = async () => {
    if (!cardRef.current) return;

    try {
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: '#0d1117',
        scale: 2,
        useCORS: true,
        allowTaint: true,
        width: cardRef.current.offsetWidth,
        height: cardRef.current.offsetHeight,
      });

      const link = document.createElement('a');
      link.download = `account-credentials-${username}-${Date.now()}.jpg`;
      link.href = canvas.toDataURL('image/jpeg', 0.9);
      link.click();
      
      toast.success('Account credentials downloaded as JPEG image!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download credentials');
    }
  };

  const handleContinue = () => {
    if (!confirmed) {
      toast.error('Please confirm that you have saved your credentials and recovery phrase');
      return;
    }
    navigate('/internal-dashboard');
  };

  return (
    <div className="flex flex-col min-h-screen bg-black w-full">
      <Header className="hidden md:block" />
      <MobileHeader />
      
      <main className="flex-grow flex items-center justify-center py-6 md:py-12 w-full px-4 md:px-6 pb-20 md:pb-6">
        <div className="w-full max-w-4xl mx-auto space-y-6">
          <RegistrationSuccessHeader username={username} />
          <SecurityWarning />

          <Card 
            ref={cardRef}
            className="w-full bg-[#0d1117] border-2 border-gray-700 shadow-2xl"
          >
            <CardHeader className="border-b border-gray-700 text-center">
              <CardTitle className="text-2xl text-sports-green flex items-center justify-center gap-2">
                🔐 Your Account Credentials
              </CardTitle>
              <p className="text-gray-400">
                Save this information in a secure location
              </p>
            </CardHeader>
            
            <CardContent className="pt-6 space-y-6">
              <ToggleableSection
                title="Login Credentials"
                isVisible={showCredentials}
                onToggle={() => setShowCredentials(!showCredentials)}
              >
                <CredentialsDisplay 
                  username={username}
                  password={password}
                  pin={pin}
                />
              </ToggleableSection>

              <ToggleableSection
                title="12-Word Recovery Phrase"
                isVisible={showSeedPhrase}
                onToggle={() => setShowSeedPhrase(!showSeedPhrase)}
              >
                <SeedPhraseDisplay seedPhrase={seedPhrase} />
              </ToggleableSection>

              {(showCredentials || showSeedPhrase) && (
                <ActionButtons
                  copied={copied}
                  onCopy={handleCopyCredentials}
                  onDownload={handleDownload}
                />
              )}
            </CardContent>
          </Card>

          <ConfirmationCheckbox
            confirmed={confirmed}
            onConfirmedChange={setConfirmed}
          />

          <div className="text-center">
            <Button 
              onClick={handleContinue}
              disabled={!confirmed}
              className="bg-sports-green text-black hover:opacity-90 px-8 py-3 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Continue to Dashboard
            </Button>
          </div>

          <SecurityTips />
        </div>
      </main>
      
      <Footer />
      <MobileBottomNav />
    </div>
  );
};

export default InternalRegistrationSuccess;
